/**
 * SectionProseEditor — inline editor that replaces a section's prose region
 * when the CEO clicks Refine prose. On save, the current verdict carries
 * over (override is preserved), the prose_override is written, and
 * override_source = 'prose_paste' tags this as a higher-conviction signal.
 *
 * Authority:
 *   docs/agents/pitch-deck-review/pages/DEAL_EVALUATOR_PAGE_SPEC_V2.md §15 — TC-DS-CAL-03
 *
 * Token bindings (zero hex):
 *   textarea     → ui/textarea.jsx + DS_TYPOGRAPHY.bodyMd + DS_COLORS.border.default
 *   cancel link  → DS_COLORS.text.secondary + DS_TYPOGRAPHY.actionBtn
 *   save button  → DS_COLORS.text.brand fill (Primary) + DS_TYPOGRAPHY.actionBtn
 *   unsaved tag  → VERDICT_PILL.moderate.text + DS_TYPOGRAPHY.sectionSubLabel
 *   spacing      → DS_SPACING.{paragraphSpacing, buttonGap, minTouchTarget}
 *   loading      → UI_BUTTON_STATE.LOADING
 *
 * Strings: AGENT_CONFIG.deal_evaluator.proseEditor from task-display-config.js.
 */

import React, { useEffect, useRef, useState } from 'react';
import { Textarea } from '../ui/textarea';
import {
  DS_BORDERS,
  DS_COLORS,
  DS_SPACING,
  DS_TYPOGRAPHY,
  UI_BUTTON_STATE,
  VERDICT_PILL,
} from '../../config/ui-invariants';
import { AGENT_CONFIG } from '../../config/task-display-config';
import { overrideSectionVerdict, persistProseRefineDelta } from '../../lib/api';

const EDITOR_CFG = AGENT_CONFIG.deal_evaluator.proseEditor;
const FOOTER_CFG = AGENT_CONFIG.deal_evaluator.calibrationFooter;

const containerStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: `${DS_SPACING.labelToContent}px`,
  marginTop: `${DS_SPACING.paragraphSpacing}px`,
};

const textareaStyle = {
  ...DS_TYPOGRAPHY.bodyMd,
  color: DS_COLORS.text.body,
  borderColor: DS_COLORS.border.default,
  resize: 'vertical',
  minHeight: EDITOR_CFG.textareaMinHeight,
  padding: '12px 14px',
};

const buttonRowStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: `${DS_SPACING.buttonGap}px`,
  flexWrap: 'wrap',
};

const cancelButtonStyle = {
  background: 'none',
  border: 'none',
  color: DS_COLORS.text.secondary,
  cursor: 'pointer',
  padding: '8px 12px',
  minHeight: `${DS_SPACING.minTouchTarget}px`,
  ...DS_TYPOGRAPHY.actionBtn,
  textDecoration: 'underline',
};

const saveButtonBaseStyle = {
  background: DS_COLORS.text.brand,
  color: DS_COLORS.surface.white,
  border: `1.5px solid ${DS_COLORS.border.brand}`,
  borderRadius: DS_BORDERS.radiusButton,
  padding: '0 20px',
  minHeight: `${DS_SPACING.minTouchTarget}px`,
  cursor: 'pointer',
  ...DS_TYPOGRAPHY.actionBtn,
  transition: 'background 200ms cubic-bezier(0.4, 0, 0.2, 1)',
};

const unsavedMarkerStyle = {
  ...DS_TYPOGRAPHY.sectionSubLabel,
  color: VERDICT_PILL.moderate.text,
  marginRight: 'auto',
};

const errorStyle = {
  ...DS_TYPOGRAPHY.sectionSubLabel,
  color: DS_COLORS.error.color,
};

export default function SectionProseEditor({
  startupId,
  sectionId,
  currentProse,
  agentVerdict,
  ceoVerdictOverride,
  onSaved,
  onCancel,
  // agentProse: the original agent-authored prose before any CEO edits.
  // Passed by DealScreenDetailView so the refine-delta capture has both sides.
  // Optional — falls back to currentProse if omitted (graceful degradation).
  agentProse,
}) {
  const textareaRef = useRef(null);
  const [draft, setDraft] = useState(currentProse || '');
  const [savingState, setSavingState] = useState('idle');
  const [errorMessage, setErrorMessage] = useState(null);
  // Track when the editor was mounted so time_to_action_seconds can be sent.
  const mountedAtRef = useRef(Date.now());

  useEffect(() => {
    setDraft(currentProse || '');
  }, [currentProse]);

  useEffect(() => {
    // autoFocus + cursor-at-end so the CEO can append without re-positioning
    const t = textareaRef.current;
    if (t) {
      const len = t.value.length;
      t.focus();
      try {
        t.setSelectionRange(len, len);
      } catch {
        // Some browsers throw if the field is not a text type — non-fatal
      }
    }
  }, []);

  const isSaving = savingState === 'saving';
  const isDirty = draft !== (currentProse || '');
  const canSave = isDirty && !isSaving && draft.trim().length > 0;

  const handleSave = async () => {
    if (!canSave) return;
    setSavingState('saving');
    setErrorMessage(null);
    // verdict carry-over rule (per spec §15 TC-DS-CAL-03):
    //   if the CEO already overrode, keep their override
    //   else fall back to the agent's current verdict
    const carriedVerdict = ceoVerdictOverride || agentVerdict;
    try {
      const response = await overrideSectionVerdict(startupId, sectionId, {
        verdict: carriedVerdict,
        prose_override: draft,
        override_source: 'prose_paste',
      });
      // WI-4: persist the prose refinement delta as a learning signal so the
      // director can learn what the CEO rewrites vs. accepts.  Non-fatal —
      // a calibration write must never break a CEO action.
      try {
        const originalProse = agentProse || currentProse || '';
        const timeToAction = Math.round((Date.now() - mountedAtRef.current) / 1000);
        await persistProseRefineDelta(startupId, sectionId, {
          agent_prose: originalProse,
          refined_prose: draft,
          time_to_action_seconds: timeToAction,
        });
      } catch (_deltaErr) {
        // Non-fatal: calibration capture failure must never block the CEO action.
      }
      onSaved?.({
        ceo_prose_override: draft,
        ceo_verdict_override: response.ceo_verdict || carriedVerdict,
        ceo_verdict_overridden_at: response.overridden_at,
      });
      setSavingState('idle');
    } catch (err) {
      setSavingState('error');
      setErrorMessage(FOOTER_CFG.toasts.error);
    }
  };

  const saveButtonStyle = canSave
    ? saveButtonBaseStyle
    : {
        ...saveButtonBaseStyle,
        opacity: UI_BUTTON_STATE.DISABLED.opacity,
        cursor: UI_BUTTON_STATE.DISABLED.cursor,
        pointerEvents: UI_BUTTON_STATE.DISABLED.pointerEvents,
      };

  const loadingSaveStyle = isSaving
    ? {
        opacity: UI_BUTTON_STATE.LOADING.opacity,
        cursor: UI_BUTTON_STATE.LOADING.cursor,
        pointerEvents: UI_BUTTON_STATE.LOADING.pointerEvents,
      }
    : {};

  return (
    <div style={containerStyle} data-testid="section-prose-editor">
      <Textarea
        ref={textareaRef}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        placeholder={EDITOR_CFG.textareaPlaceholder}
        maxLength={EDITOR_CFG.textareaMaxChars}
        style={textareaStyle}
        data-testid="prose-editor-textarea"
      />
      <div style={buttonRowStyle}>
        {isDirty && (
          <span style={unsavedMarkerStyle} data-testid="prose-editor-unsaved">
            {EDITOR_CFG.unsavedMarker}
          </span>
        )}
        <button
          type="button"
          onClick={onCancel}
          disabled={isSaving}
          style={cancelButtonStyle}
          data-testid="prose-editor-cancel"
        >
          {EDITOR_CFG.cancelLabel}
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={!canSave}
          style={{ ...saveButtonStyle, ...loadingSaveStyle }}
          data-testid="prose-editor-save"
        >
          {isSaving ? EDITOR_CFG.loadingLabel : EDITOR_CFG.saveLabel}
        </button>
      </div>
      {errorMessage && (
        <div style={errorStyle} role="alert" data-testid="prose-editor-error">
          {errorMessage}
        </div>
      )}
    </div>
  );
}
